import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Router } from '@angular/router';

import { Observable } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { Constants } from '../operator/operator.constant';
import { MultiBuyerConstants } from '../buyer/multibuyer.constant';
import { BuyerContextService } from '../shared/services/buyer-context/buyer-context.service';

interface RequestContext {
  token: string;
  userId: string;
}

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(
    private _router: Router,
    private _auth: AuthService,
    private _buyerContext: BuyerContextService
  ) {}

  private readonly _operatorPagesThatNeedBuyerToken: Array<string> =
    Constants.PAGES_THAT_NEED_BUYER_TOKEN;
  private readonly _operatorRoutesThatNeedBuyerToken: Array<string> =
    Constants.ROUTES_THAT_NEED_BUYER_TOKEN;

  private readonly _multiBuyerPagesForEntity: Array<string> =
    MultiBuyerConstants.PAGES_THAT_NEED_ENTITY_USER;
  private readonly _multiBuyerRoutesForEntity: Array<string> =
    MultiBuyerConstants.ROUTES_THAT_NEED_ENTITY_USER;

  private readonly _multiBuyerPagesForUmbrella: Array<string> =
    MultiBuyerConstants.PAGES_THAT_FORCE_UMBRELLA_USER;
  private readonly _multiBuyerRoutesForUmbrella: Array<string> =
    MultiBuyerConstants.ROUTES_THAT_FORCE_UMBRELLA_USER;

  private readonly _multiBuyerPagesForApiUser: Array<string> =
    MultiBuyerConstants.PAGES_THAT_NEED_API_USER;
  private readonly _multiBuyerRoutesForApiUser: Array<string> =
    MultiBuyerConstants.ROUTES_THAT_NEED_API_USER;

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.headers.get('skipInterceptor')) {
      request = request.clone({
        headers: request.headers.delete('skipInterceptor'),
      });
      return next.handle(request);
    }

    if (!this._auth.token) {
      this._router.navigateByUrl('/login');
    }

    const requestContext = this._auth.isLoggedInAsMultiBuyer()
      ? this._getMultiBuyerContext(request.url)
      : this._getOperatorContext(request.url);

    request = request.clone({
      setHeaders: {
        'access-token': requestContext.token,
        'service-user-id': requestContext.userId,
      },
    });

    return next.handle(request);
  }

  private _getMultiBuyerContext(url: string): RequestContext {
    if (this._multiBuyerRoutesForApiUser.some((route) => url.includes(route))) {
      return this._apiUserContext();
    }

    if (
      this._multiBuyerRoutesForUmbrella.some((route) => url.includes(route))
    ) {
      return this._umbrellaContext();
    }

    if (
      this._multiBuyerPagesForApiUser.some((page) =>
        this._router.url.includes(page)
      )
    ) {
      return this._apiUserContext();
    }

    if (
      this._multiBuyerPagesForUmbrella.some((page) =>
        this._router.url.includes(page)
      )
    ) {
      return this._umbrellaContext();
    }

    return this._umbrellaContext();
  }

  private _getOperatorContext(url: string): RequestContext {
    const isOperatorExclusiveRequest =
      this._operatorRoutesThatNeedBuyerToken.some((route) =>
        url.includes(route)
      );
    const isServiceBuyerTokenNeeded =
      this._operatorPagesThatNeedBuyerToken.some((page) =>
        this._router.url.includes(page)
      );

    const token =
      !isOperatorExclusiveRequest && isServiceBuyerTokenNeeded
        ? this._buyerContext.token
        : this._auth.token!;

    return {
      token,
      userId:
        this._auth.user?.id && this._auth.hasServiceOperatorAccess()
          ? this._auth.user.id.toString()
          : '',
    };
  }

  private _umbrellaContext(): RequestContext {
    return {
      token: this._auth.token!,
      userId: this._auth.user!.id.toString(),
    };
  }

  private _apiUserContext(): RequestContext {
    return {
      token: this._buyerContext.token,
      userId: this._buyerContext.loggedUserId.toString(),
    };
  }
}
